/**
 * useTasks - React Query hooks for task engine data fetching and mutations
 * Query keys all start with ['tasks', ...] for granular cache invalidation.
 * Server state only — ephemeral UI state lives in taskUIStore.ts.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import taskService from '../services/api/taskService';
import type {
  Task,
  ListTasksParams,
  TaskListResponse,
  CreateTaskDto,
  CompleteTaskDto,
  SkipTaskDto,
  RescheduleTaskDto,
  ReassignTaskDto,
  AcknowledgeTaskDto,
  HoldTaskDto,
} from '../types/tasks';

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Paginated, filterable task list.
 * Used by both TaskSchedulerPage and MyTasksPage.
 */
export function useTaskList(
  params: ListTasksParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['tasks', 'list', params],
    queryFn: () => taskService.getTasks(params),
    staleTime: 30_000,
    ...options,
  });
}

/**
 * Single task detail.
 * For use in a future detail drawer or modal.
 */
export function useTask(taskId: string, initialData?: Task) {
  return useQuery({
    queryKey: ['tasks', 'detail', taskId],
    queryFn: () => taskService.getTask(taskId),
    enabled: !!taskId,
    // When the caller already has the data (e.g. MyTaskDetailPanel pre-fetches before
    // delegating to TaskDetailModal), seed the cache so the modal skips its own fetch.
    initialData,
    // Mark seeded data as perpetually fresh so a second subscriber (TaskDetailModal)
    // does not fire an additional GET /tasks/:id at staleTime=0.
    staleTime: initialData ? Infinity : undefined,
    initialDataUpdatedAt: initialData ? Date.now() : undefined,
  });
}

/**
 * Task summary counts for dashboard metric cards.
 * Refreshes more aggressively than the task list (15s stale time).
 * assigneeId is included in the query key for future cache segmentation;
 * the current API endpoint is org-scoped and does not accept an assignee filter.
 */
export function useTaskSummary(assigneeId?: string) {
  return useQuery({
    queryKey: ['tasks', 'summary', assigneeId],
    queryFn: () => taskService.getTaskSummary(),
    staleTime: 15_000,
  });
}

/**
 * Admin workload view — tasks per provider with over-threshold flags.
 */
export function useTaskWorkload() {
  return useQuery({
    queryKey: ['tasks', 'workload'],
    queryFn: () => taskService.getWorkload(),
    staleTime: 60_000,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create an ad-hoc task.
 * Invalidates the task list and summary counts on success.
 */
export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateTaskDto) => taskService.createTask(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'summary'] });
    },
  });
}

/**
 * Mark a task as completed.
 * Invalidates the task detail, list, and summary on success.
 */
export function useCompleteTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, dto }: { taskId: string; dto: CompleteTaskDto }) =>
      taskService.completeTask(taskId, dto),
    onSuccess: (_data, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'summary'] });
    },
  });
}

/**
 * Skip a task (with optional cascade).
 */
export function useSkipTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, dto }: { taskId: string; dto: SkipTaskDto }) =>
      taskService.skipTask(taskId, dto),
    onSuccess: (_data, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'summary'] });
    },
  });
}

/**
 * Reschedule a task to a new date.
 */
export function useRescheduleTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, dto }: { taskId: string; dto: RescheduleTaskDto }) =>
      taskService.rescheduleTask(taskId, dto),
    onSuccess: (_data, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'list'] });
    },
  });
}

/**
 * Reassign a task to a different user.
 */
export function useReassignTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, dto }: { taskId: string; dto: ReassignTaskDto }) =>
      taskService.reassignTask(taskId, dto),
    onSuccess: (_data, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'workload'] });
    },
  });
}

/**
 * Toggle the pinned state of a task.
 * Uses an optimistic update: immediately flips `pinned` in the cache,
 * reverts on error, then settles with a full list invalidation.
 */
export function useTogglePin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: string) => taskService.togglePin(taskId),

    onMutate: async (taskId) => {
      // Cancel any in-flight list fetches to prevent overwriting the optimistic update
      await queryClient.cancelQueries({ queryKey: ['tasks', 'list'] });

      // Snapshot all current task list cache entries
      const previousLists = queryClient.getQueriesData<TaskListResponse>({
        queryKey: ['tasks', 'list'],
      });

      // Optimistically toggle pinned on every cached list entry
      queryClient.setQueriesData<TaskListResponse>(
        { queryKey: ['tasks', 'list'] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((task) =>
              task.id === taskId ? { ...task, pinned: !task.pinned } : task,
            ),
          };
        },
      );

      // Return snapshot so onError can revert
      return { previousLists };
    },

    onError: (_err, _taskId, context) => {
      if (context?.previousLists) {
        for (const [queryKey, previousData] of context.previousLists) {
          queryClient.setQueryData(queryKey, previousData);
        }
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'list'] });
    },
  });
}

/**
 * Acknowledge a task (e.g., "Working on it", "Blocked externally").
 */
export function useAcknowledgeTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, dto }: { taskId: string; dto: AcknowledgeTaskDto }) =>
      taskService.acknowledgeTask(taskId, dto),
    onSuccess: (_data, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'list'] });
    },
  });
}

/**
 * Request a clinical hold on a task.
 */
export function useRequestHold() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, dto }: { taskId: string; dto: HoldTaskDto }) =>
      taskService.requestHold(taskId, dto),
    onSuccess: (_data, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'summary'] });
    },
  });
}

// ============================================================================
// Watcher Hooks
// ============================================================================

/**
 * Fetch the watcher list for a task.
 * Query key: ['tasks', 'watchers', taskId]
 */
export function useGetWatchers(taskId: string) {
  return useQuery({
    queryKey: ['tasks', 'watchers', taskId],
    queryFn: () => taskService.getWatchers(taskId),
    enabled: !!taskId,
  });
}

/**
 * Add a watcher to a task.
 * Invalidates both the watchers list and the task detail cache.
 */
export function useAddWatcher() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, userId }: { taskId: string; userId: string }) =>
      taskService.addWatcher(taskId, userId),
    onSuccess: (_data, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'watchers', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] });
      // Invalidate the watched-task list so the "Watching" section on My Tasks
      // reflects changes immediately when the current user adds/removes themselves.
      queryClient.invalidateQueries({ queryKey: ['tasks', 'list'] });
    },
  });
}

/**
 * Remove a watcher from a task.
 * Invalidates the watchers list, task detail, and watched-task list caches.
 */
export function useRemoveWatcher() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, userId }: { taskId: string; userId: string }) =>
      taskService.removeWatcher(taskId, userId),
    onSuccess: (_data, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'watchers', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] });
      // Invalidate the watched-task list so the "Watching" section on My Tasks
      // reflects changes immediately when the current user adds/removes themselves.
      queryClient.invalidateQueries({ queryKey: ['tasks', 'list'] });
    },
  });
}
